import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { JobApplication } from './ApplicationWizard'
import { jobApplicationService, ResumeFitAnalysisResponse, ResumeFitAnalysis, ResumeSelection, TailoredResumeResponse } from '../services/jobApplicationService'
import { useResumes } from '../hooks/useResumes'
import { ResumeItem } from '../services/apiService'
import { AssignedResumeViewer } from './AssignedResumeViewer'
import { TailoredResumeWizard } from './TailoredResumeWizard'
import { LoadingSpinner } from './LoadingSpinner'
import { useMessageBox } from './MessageBox'
import { sendDebugLog } from '../utils/debugLogging'
import './ResumeAssignmentModal.css'

const INITIAL_FIT_ANALYSIS_POLL_INTERVAL_MS = 3000
const INITIAL_FIT_ANALYSIS_MAX_WAIT_MS = 300000

interface ResumeAssignmentModalProps {
  isOpen: boolean
  onClose: () => void
  application: JobApplication
  onResumeUpdated?: () => void
}

export const ResumeAssignmentModal: React.FC<ResumeAssignmentModalProps> = ({
  isOpen,
  onClose,
  application,
  onResumeUpdated,
}) => {
  // #region agent log
  sendDebugLog({message:'ResumeAssignmentModal render',data:{isOpen,hasApplication:!!application,applicationId:application?.id}});
  // #endregion
  
  const { resumes: regularResumes } = useResumes()
  const { showMessageBox } = useMessageBox()
  const [fitAnalysis, setFitAnalysis] = useState<ResumeFitAnalysisResponse | null>(null)
  const [isLoadingFitAnalysis, setIsLoadingFitAnalysis] = useState(false)
  const [fitAnalysisError, setFitAnalysisError] = useState<string | null>(null)
  const [isMounted, setIsMounted] = useState(false)
  const [resumeDetailsResumeId, setResumeDetailsResumeId] = useState<string | null>(null)
  
  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (!isOpen) {
      setResumeDeleteConfirmId(null)
      setResumeDetailsResumeId(null)
    }
  }, [isOpen])
  const [tailoredResume, setTailoredResume] = useState<TailoredResumeResponse | null>(null)
  const [isTailoredResumeWizardOpen, setIsTailoredResumeWizardOpen] = useState(false)
  const [isSelectingResume, setIsSelectingResume] = useState(false)
  const [generatingFitScores, setGeneratingFitScores] = useState<Set<string>>(new Set())
  const fitScorePollIntervalsRef = useRef<Map<string, NodeJS.Timeout>>(new Map())
  const [applicationData, setApplicationData] = useState<JobApplication>(application)
  const [resumeDeleteConfirmId, setResumeDeleteConfirmId] = useState<string | null>(null)
  const [isDeletingTailored, setIsDeletingTailored] = useState(false)
  const [deletingTailoredId, setDeletingTailoredId] = useState<string | null>(null)
  
  // Track application prop changes to prevent unnecessary re-renders
  const applicationIdRef = useRef<string | undefined>(application.id)
  const renderCountRef = useRef(0)
  renderCountRef.current += 1

  // Update applicationData when application prop changes (but only if ID is different)
  useEffect(() => {
    if (application.id !== applicationIdRef.current) {
      sendDebugLog({
        location: 'ResumeAssignmentModal.tsx:applicationData-update',
        message: 'Application ID changed, updating applicationData',
        data: {
          renderCount: renderCountRef.current,
          oldId: applicationIdRef.current,
          newId: application.id,
        },
        timestamp: Date.now(),
        sessionId: 'debug-session',
        runId: 'run1'
      })
      applicationIdRef.current = application.id
      setApplicationData(application)
    }
  }, [application])

  // Reload application data when modal opens to ensure we have the latest selection state
  useEffect(() => {
    if (!isOpen || !application.id) return

    const reloadApplicationData = async () => {
      try {
        const updatedApplication = await jobApplicationService.getApplication(application.id!)
        setApplicationData(updatedApplication as JobApplication)
      } catch (error) {
        console.error('Failed to reload application data:', error)
      }
    }

    reloadApplicationData()
  }, [isOpen, application.id])

  // Combine regular resumes with tailored resume (only when actually generated/editing - not not_started or wizard_in_progress)
  const availableResumes = useMemo(() => {
    const resumes = [...regularResumes]
    const isTailoredReady =
      tailoredResume &&
      (tailoredResume.status === 'generated' || tailoredResume.status === 'editing')

    if (isTailoredReady) {
      const baseResume = regularResumes.find(r => r.id === tailoredResume!.baseResumeId)
      const baseResumeName = baseResume?.originalFilename || baseResume?.filename || 'Resume'

      const virtualResume: ResumeItem = {
        id: `tailored-${tailoredResume!.jobApplicationId}`,
        originalFilename: `Custom Tailored Resume (based on ${baseResumeName})`,
        filename: `Custom Tailored Resume (based on ${baseResumeName})`,
        resumeType: 'tailored',
        uploadedAt: tailoredResume!.createdAt,
        atsProcessingStatus: 'tailored' as any,
      }

      resumes.push(virtualResume)
    }

    return resumes
  }, [regularResumes, tailoredResume])

  // Load fit analysis - use ref to track if we've already loaded for this application
  const lastLoadedApplicationIdRef = useRef<string | null>(null)
  const fitAnalysisPollIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const fitAnalysisPollTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasFitAnalysisDataRef = useRef(false)
  const hasRequestedFitAnalysisRef = useRef(false)
  
  useEffect(() => {
    if (!isOpen || !application.id) {
      // Reset when modal closes
      if (!isOpen) {
        lastLoadedApplicationIdRef.current = null
        hasFitAnalysisDataRef.current = false
        hasRequestedFitAnalysisRef.current = false
        setIsLoadingFitAnalysis(false)
        if (fitAnalysisPollIntervalRef.current) {
          clearInterval(fitAnalysisPollIntervalRef.current)
          fitAnalysisPollIntervalRef.current = null
        }
        if (fitAnalysisPollTimeoutRef.current) {
          clearTimeout(fitAnalysisPollTimeoutRef.current)
          fitAnalysisPollTimeoutRef.current = null
        }
      }
      return
    }

    // Skip if we've already loaded for this application ID
    if (lastLoadedApplicationIdRef.current === application.id && hasFitAnalysisDataRef.current) {
      sendDebugLog({
        location: 'ResumeAssignmentModal.tsx:fit-analysis-effect',
        message: 'Skipping fit analysis load - already loaded for this application',
        data: {
          renderCount: renderCountRef.current,
          applicationId: application.id,
          lastLoadedId: lastLoadedApplicationIdRef.current,
          hasData: hasFitAnalysisDataRef.current,
        },
        timestamp: Date.now(),
        sessionId: 'debug-session',
        runId: 'run1'
      })
      return
    }

    sendDebugLog({
      location: 'ResumeAssignmentModal.tsx:fit-analysis-effect',
      message: 'Starting fit analysis load',
      data: {
        renderCount: renderCountRef.current,
        applicationId: application.id,
        isOpen,
        lastLoadedId: lastLoadedApplicationIdRef.current,
      },
      timestamp: Date.now(),
      sessionId: 'debug-session',
      runId: 'run1'
    })

    // Reset data flag if application ID changed
    if (lastLoadedApplicationIdRef.current !== application.id) {
      hasFitAnalysisDataRef.current = false
      hasRequestedFitAnalysisRef.current = false
      setFitAnalysis(null)
    }
    
    lastLoadedApplicationIdRef.current = application.id
    let isCancelled = false

    const loadFitAnalysis = async () => {
      if (isCancelled) return
      
      // Skip if we already have data
      if (hasFitAnalysisDataRef.current) {
        if (fitAnalysisPollIntervalRef.current) {
          clearInterval(fitAnalysisPollIntervalRef.current)
          fitAnalysisPollIntervalRef.current = null
        }
        if (fitAnalysisPollTimeoutRef.current) {
          clearTimeout(fitAnalysisPollTimeoutRef.current)
          fitAnalysisPollTimeoutRef.current = null
        }
        return
      }
      
      if (!hasRequestedFitAnalysisRef.current) {
        setIsLoadingFitAnalysis(true)
        hasRequestedFitAnalysisRef.current = true
      }
      
      sendDebugLog({
        location: 'ResumeAssignmentModal.tsx:loadFitAnalysis',
        message: 'Loading fit analysis',
        data: {
          applicationId: application.id,
          isCancelled,
          hasData: hasFitAnalysisDataRef.current,
        },
        timestamp: Date.now(),
        sessionId: 'debug-session',
        runId: 'run1'
      })
      
      setFitAnalysisError(null)
      
      try {
        const analysis = await jobApplicationService.getResumeFitAnalysis(application.id!)
        if (isCancelled) return
        
        // Check if we have valid data
        const hasData = analysis?.resumeFitAnalyses && analysis.resumeFitAnalyses.length > 0
        const hasTerminalNoResumesState = analysis?.status === 'no_resumes' || !!analysis?.noResumesMessage
        const hasErrorState = analysis?.status === 'error'
        
        sendDebugLog({
          location: 'ResumeAssignmentModal.tsx:loadFitAnalysis',
          message: 'Fit analysis loaded successfully',
          data: {
            applicationId: application.id,
            hasAnalyses: hasData,
            analysisCount: analysis?.resumeFitAnalyses?.length || 0,
            status: analysis?.status || 'unknown',
            errorMessage: analysis?.errorMessage || null,
          },
          timestamp: Date.now(),
          sessionId: 'debug-session',
          runId: 'run1'
        })
        
        setFitAnalysis(analysis)

        if (hasErrorState) {
          setFitAnalysisError(
            analysis?.errorMessage || analysis?.message || 'Resume fit analysis failed'
          )
          setIsLoadingFitAnalysis(false)
          if (fitAnalysisPollIntervalRef.current) {
            clearInterval(fitAnalysisPollIntervalRef.current)
            fitAnalysisPollIntervalRef.current = null
          }
          if (fitAnalysisPollTimeoutRef.current) {
            clearTimeout(fitAnalysisPollTimeoutRef.current)
            fitAnalysisPollTimeoutRef.current = null
          }
          return
        }

        setFitAnalysisError(null)
        // Keep the first network request visible, but do not block or dominate
        // the modal while background polling continues.
        setIsLoadingFitAnalysis(false)
        
        // Mark terminal states so polling stops.
        if (hasData || hasTerminalNoResumesState) {
          hasFitAnalysisDataRef.current = true
          if (fitAnalysisPollIntervalRef.current) {
            clearInterval(fitAnalysisPollIntervalRef.current)
            fitAnalysisPollIntervalRef.current = null
          }
          if (fitAnalysisPollTimeoutRef.current) {
            clearTimeout(fitAnalysisPollTimeoutRef.current)
            fitAnalysisPollTimeoutRef.current = null
          }
        }
      } catch (error) {
        if (isCancelled) return
        
        sendDebugLog({
          location: 'ResumeAssignmentModal.tsx:loadFitAnalysis',
          message: 'Fit analysis load error',
          data: {
            applicationId: application.id,
            error: error instanceof Error ? error.message : String(error),
            is404: error instanceof Error && (error.message.includes('not found') || error.message.includes('404')),
          },
          timestamp: Date.now(),
          sessionId: 'debug-session',
          runId: 'run1'
        })
        
        if (error instanceof Error && (error.message.includes('not found') || error.message.includes('404'))) {
          setFitAnalysisError(null)
          setIsLoadingFitAnalysis(false)
          // Keep polling for 404s
        } else {
          setFitAnalysisError(error instanceof Error ? error.message : 'Failed to load fit analysis')
          setFitAnalysis(null)
          setIsLoadingFitAnalysis(false)
        }
      }
    }

    // Initial load
    loadFitAnalysis()

    // Poll for fit analysis - only if we don't have data yet
    if (application.id && !hasFitAnalysisDataRef.current) {
      // Clear any existing interval
      if (fitAnalysisPollIntervalRef.current) {
        clearInterval(fitAnalysisPollIntervalRef.current)
        fitAnalysisPollIntervalRef.current = null
      }
      if (fitAnalysisPollTimeoutRef.current) {
        clearTimeout(fitAnalysisPollTimeoutRef.current)
        fitAnalysisPollTimeoutRef.current = null
      }
      
      fitAnalysisPollIntervalRef.current = setInterval(() => {
        if (isCancelled) return
        loadFitAnalysis()
      }, INITIAL_FIT_ANALYSIS_POLL_INTERVAL_MS)

      fitAnalysisPollTimeoutRef.current = setTimeout(() => {
        if (fitAnalysisPollIntervalRef.current) {
          clearInterval(fitAnalysisPollIntervalRef.current)
          fitAnalysisPollIntervalRef.current = null
        }
        fitAnalysisPollTimeoutRef.current = null
        if (!isCancelled && !hasFitAnalysisDataRef.current) {
          setIsLoadingFitAnalysis(false)
          setFitAnalysisError(
            'Resume fit analysis is taking longer than expected. It may still be processing, or the background analysis may have failed. Try reprocessing the fit analysis.'
          )
        }
      }, INITIAL_FIT_ANALYSIS_MAX_WAIT_MS)
    }

    return () => {
      isCancelled = true
      if (fitAnalysisPollIntervalRef.current) {
        clearInterval(fitAnalysisPollIntervalRef.current)
        fitAnalysisPollIntervalRef.current = null
      }
      if (fitAnalysisPollTimeoutRef.current) {
        clearTimeout(fitAnalysisPollTimeoutRef.current)
        fitAnalysisPollTimeoutRef.current = null
      }
    }
  }, [isOpen, application.id])

  // Load tailored resume
  useEffect(() => {
    // #region agent log
    sendDebugLog({message:'loadTailoredResume useEffect triggered',data:{isOpen,hasApplicationId:!!application.id,applicationId:application?.id}});
    // #endregion
    
    if (!isOpen || !application.id) {
      // #region agent log
      sendDebugLog({message:'Skipping tailored resume load',data:{isOpen,hasApplicationId:!!application.id}});
      // #endregion
      return
    }

    const loadTailoredResume = async () => {
      // #region agent log
      sendDebugLog({message:'Starting tailored resume load',data:{applicationId:application.id}});
      // #endregion
      try {
        const resume = await jobApplicationService.getTailoredResume(application.id!)
        // #region agent log
        sendDebugLog({message:'Tailored resume loaded successfully',data:{hasResume:!!resume}});
        // #endregion
        setTailoredResume(resume)
      } catch (error) {
        // #region agent log
        sendDebugLog({message:'Tailored resume load error',data:{error:error instanceof Error ? error.message : String(error),is404:error instanceof Error && error.message.includes('404')}});
        // #endregion
        // 404 is expected if no resume exists
        if (error instanceof Error && !error.message.includes('404')) {
          console.error('Failed to load tailored resume:', error)
        }
        setTailoredResume(null)
      }
    }

    loadTailoredResume()
  }, [isOpen, application.id])

  // Reload application data when resume selection changes
  const handleResumeSelectionChange = useCallback(async (selection: ResumeSelection) => {
    if (!application.id) return

    try {
      await jobApplicationService.updateResumeSelection(application.id, selection)
      
      // Reload application data
      const updatedApplication = await jobApplicationService.getApplication(application.id)
      setApplicationData(updatedApplication as JobApplication)
      
      showMessageBox({
        type: 'success',
        title: 'Selection Saved',
        message: 'Resume selection has been saved successfully.',
        buttons: ['ok'],
        onButtonClick: () => {}
      })

      onResumeUpdated?.()
    } catch (error) {
      showMessageBox({
        type: 'error',
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update resume selection.',
        buttons: ['ok'],
        onButtonClick: () => {}
      })
      throw error
    }
  }, [application.id, showMessageBox, onResumeUpdated])

  const handleTailoredResumeUpdated = useCallback(async () => {
    if (!application.id) return

    try {
      const updatedTailoredResume = await jobApplicationService.getTailoredResume(application.id)
      setTailoredResume(updatedTailoredResume)
      
      const updatedApplication = await jobApplicationService.getApplication(application.id)
      setApplicationData(updatedApplication as JobApplication)
      
      onResumeUpdated?.()
    } catch (error) {
      console.error('Failed to reload tailored resume:', error)
    }
  }, [application.id, onResumeUpdated])

  const handleTailoredResumeGenerated = useCallback((resume: TailoredResumeResponse) => {
    sendDebugLog({
      location: 'ResumeAssignmentModal.tsx:handleTailoredResumeGenerated',
      message: 'Tailored resume generated',
      data: {
        applicationId: application.id,
        resumeId: resume.jobApplicationId,
      },
      timestamp: Date.now(),
      sessionId: 'debug-session',
      runId: 'run1'
    })
    
    // Update tailored resume state
    setTailoredResume(resume)
    
    // Update application data
    setApplicationData(prev => ({
      ...prev,
      customResumeStatus: 'generated' as const,
      resumeSelectionType: 'custom' as const,
    }))
    
    // Close wizard
    setIsTailoredResumeWizardOpen(false)
    
    // Reload application data
    handleTailoredResumeUpdated()
    
    showMessageBox({
      type: 'success',
      title: 'Resume Generated',
      message: 'Your tailored resume has been generated successfully!',
      buttons: ['ok'],
      onButtonClick: () => {}
    })
  }, [application.id, handleTailoredResumeUpdated, showMessageBox])

  // Handle escape key and prevent body scroll
  useEffect(() => {
    if (!isOpen) return

    const shouldLockBodyScroll =
      typeof window !== 'undefined' && window.innerWidth <= 768
    const previousOverflow = document.body.style.overflow

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    if (shouldLockBodyScroll) {
      document.body.style.overflow = 'hidden'
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = previousOverflow
      // Cleanup any polling intervals
      fitScorePollIntervalsRef.current.forEach((interval) => {
        clearInterval(interval)
      })
      fitScorePollIntervalsRef.current.clear()
    }
  }, [isOpen, onClose])

  if (!isOpen) {
    return null
  }

  // Filter available resumes - exclude the currently selected one from the selection list
  const availableResumesForSelection = useMemo(() => {
    // If a saved resume is selected, exclude it from the list
    if (applicationData.resumeSelectionType === 'saved' && applicationData.selectedResumeId) {
      return availableResumes.filter(r => r.id !== applicationData.selectedResumeId)
    }
    // If a custom/tailored resume is selected, exclude it from the list
    if (applicationData.resumeSelectionType === 'custom' && tailoredResume) {
      const tailoredResumeId = `tailored-${tailoredResume.jobApplicationId}`
      return availableResumes.filter(r => r.id !== tailoredResumeId)
    }
    return availableResumes
  }, [availableResumes, applicationData.selectedResumeId, applicationData.resumeSelectionType, tailoredResume])

  const currentAssignedResumeDetailsId = useMemo(() => {
    if (applicationData.resumeSelectionType === 'custom' && tailoredResume) {
      return `tailored-${tailoredResume.jobApplicationId}`
    }

    return applicationData.selectedResumeId || null
  }, [applicationData.resumeSelectionType, applicationData.selectedResumeId, tailoredResume])

  const previewedResume = useMemo(() => {
    if (!resumeDetailsResumeId) {
      return null
    }

    return availableResumes.find(resume => resume.id === resumeDetailsResumeId) || null
  }, [availableResumes, resumeDetailsResumeId])

  const previewedResumeSelectionType = useMemo<'saved' | 'custom' | null>(() => {
    if (!previewedResume) {
      return null
    }

    return previewedResume.id.startsWith('tailored-') ? 'custom' : 'saved'
  }, [previewedResume])

  const canOpenResumeDetails = !!previewedResume && (
    previewedResumeSelectionType !== 'custom' || !!tailoredResume
  )

  useEffect(() => {
    if (resumeDetailsResumeId && !canOpenResumeDetails) {
      setResumeDetailsResumeId(null)
    }
  }, [resumeDetailsResumeId, canOpenResumeDetails])

  useEffect(() => {
    if (!resumeDeleteConfirmId) return
    const stillExists = availableResumes.some(r => r.id === resumeDeleteConfirmId)
    if (!stillExists) {
      setResumeDeleteConfirmId(null)
    }
  }, [availableResumes, resumeDeleteConfirmId])

  const getFitScoreColor = (score: number): string => {
    if (score >= 80) return 'excellent'
    if (score >= 60) return 'good'
    if (score >= 40) return 'moderate'
    return 'low'
  }

  const handleDeleteResume = useCallback(async (resume: ResumeItem) => {
    if (!resume.id || !application.id) return
    if (!resume.id.startsWith('tailored-')) return
    const jobApplicationId = resume.id.replace('tailored-', '')
    try {
      setIsDeletingTailored(true)
      setDeletingTailoredId(resume.id)
      await jobApplicationService.deleteTailoredResume(jobApplicationId)
      setTailoredResume(null)
      setApplicationData(prev => ({
        ...prev,
        resumeSelectionType: null,
        selectedResumeId: null,
        customResumeStatus: null,
      }))
      setFitAnalysis(prev => {
        if (!prev?.resumeFitAnalyses) return prev
        const filtered = prev.resumeFitAnalyses.filter(analysis =>
          analysis.resumeId !== resume.id && analysis.resumeId !== jobApplicationId
        )
        return { ...prev, resumeFitAnalyses: filtered }
      })
      onResumeUpdated?.()
      showMessageBox({
        type: 'success',
        title: 'Tailored Resume Deleted',
        message: 'The tailored resume has been deleted for this job application.',
        buttons: ['ok'],
        onButtonClick: () => {}
      })
    } catch (error) {
      showMessageBox({
        type: 'error',
        title: 'Delete Failed',
        message: error instanceof Error ? error.message : 'Failed to delete tailored resume.',
        buttons: ['ok'],
        onButtonClick: () => {}
      })
    } finally {
      setResumeDeleteConfirmId(null)
      setIsDeletingTailored(false)
      setDeletingTailoredId(null)
    }
  }, [application.id, onResumeUpdated, showMessageBox])


  // Get fit score for a specific resume (including tailored resumes)
  const getResumeFitScore = (resumeId: string): ResumeFitAnalysis | null => {
    if (!fitAnalysis?.resumeFitAnalyses) return null
    // For tailored resumes, check both the virtual ID and the actual job application ID
    if (resumeId.startsWith('tailored-')) {
      const jobAppId = resumeId.replace('tailored-', '')
      return fitAnalysis.resumeFitAnalyses.find(analysis => 
        analysis.resumeId === resumeId || analysis.resumeId === jobAppId
      ) || null
    }
    return fitAnalysis.resumeFitAnalyses.find(analysis => analysis.resumeId === resumeId) || null
  }

  // Get best match resume from fit analysis
  const bestMatchResume = useMemo(() => {
    if (!fitAnalysis?.resumeFitAnalyses || fitAnalysis.resumeFitAnalyses.length === 0) {
      return null
    }
    const sortedAnalyses = [...fitAnalysis.resumeFitAnalyses].sort((a, b) => b.fitScore - a.fitScore)
    return sortedAnalyses[0]
  }, [fitAnalysis])

  // Handle resume selection
  const handleResumeSelect = useCallback(async (resumeId: string) => {
    if (!application.id || isSelectingResume) return

    setIsSelectingResume(true)
    try {
      // Check if it's a tailored resume
      const isTailored = resumeId.startsWith('tailored-')
      await handleResumeSelectionChange({
        type: isTailored ? 'custom' : 'saved',
        resumeId: isTailored ? null : resumeId
      })
    } catch (error) {
      // Error already handled in handleResumeSelectionChange
    } finally {
      setIsSelectingResume(false)
    }
  }, [application.id, isSelectingResume, handleResumeSelectionChange])

  // Auto-select best match resume if no resume is currently selected
  // This runs when modal opens and fit analysis is ready
  const hasAutoSelectedRef = useRef(false)
  const hasCheckedInitialSelectionRef = useRef(false)
  
  useEffect(() => {
    if (!isOpen || !applicationData.id) {
      // Reset flags when modal closes
      hasAutoSelectedRef.current = false
      hasCheckedInitialSelectionRef.current = false
      return
    }

    // If there's already a selection, preserve it (it's already in applicationData)
    const hasExistingSelection = applicationData.resumeSelectionType || applicationData.selectedResumeId || tailoredResume
    
    if (hasExistingSelection) {
      // Resume is already selected - no need to auto-select
      hasCheckedInitialSelectionRef.current = true
      return
    }

    // No existing selection - check if we can auto-select best match
    if (
      !bestMatchResume ||
      isSelectingResume ||
      isLoadingFitAnalysis ||
      hasAutoSelectedRef.current ||
      !fitAnalysis // Wait for fit analysis to be loaded
    ) {
      return
    }

    // Auto-select the best match resume only once
    const bestMatchResumeId = bestMatchResume.resumeId
    const matchingResume = availableResumes.find(r => r.id === bestMatchResumeId)
    
    if (matchingResume && !matchingResume.id.startsWith('tailored-')) {
      hasAutoSelectedRef.current = true
      hasCheckedInitialSelectionRef.current = true
      handleResumeSelect(bestMatchResumeId)
    }
  }, [
    isOpen,
    bestMatchResume,
    applicationData.resumeSelectionType,
    applicationData.selectedResumeId,
    applicationData.id,
    availableResumes,
    isSelectingResume,
    isLoadingFitAnalysis,
    handleResumeSelect,
    fitAnalysis,
    tailoredResume
  ])

  // Handle generating fit score for a specific resume (including tailored resumes)
  const handleGenerateFitScore = useCallback(async (resumeId: string) => {
    if (!application.id || generatingFitScores.has(resumeId)) return

    const applicationId = application.id
    setGeneratingFitScores(prev => new Set(prev).add(resumeId))
    setFitAnalysisError(null)

    try {
      // For tailored resumes, we need to use the job application ID as the resume ID
      // The backend will handle analyzing the tailored resume
      const actualResumeId = resumeId.startsWith('tailored-') 
        ? resumeId.replace('tailored-', '') 
        : resumeId
      
      // Trigger reprocess for this resume only
      await jobApplicationService.reprocessResumeFitAnalysis(applicationId, resumeId)
      
      // Poll for the fit analysis to include this resume
      let pollCount = 0
      const maxPolls = 150 // 5 minutes max (150 * 2 seconds) - allows time for multiple resumes
      const startTime = Date.now()
      const maxWaitTime = 300000 // 5 minutes in milliseconds
      
      const clearPolling = () => {
        const interval = fitScorePollIntervalsRef.current.get(resumeId)
        if (interval) {
          clearInterval(interval)
          fitScorePollIntervalsRef.current.delete(resumeId)
        }
      }
      
      const pollInterval = setInterval(async () => {
        pollCount++
        const elapsed = Date.now() - startTime
        
        try {
          const updatedFitAnalysis = await jobApplicationService.getResumeFitAnalysis(applicationId)
          
          // Check if our resume now has fit data
          // For tailored resumes, check both formats: "tailored-{jobAppId}" and just the jobAppId
          const resumeFitData = updatedFitAnalysis?.resumeFitAnalyses?.find(
            (analysis: ResumeFitAnalysis) => {
              // Exact match
              if (analysis.resumeId === resumeId) {
                console.log(`[Fit Score] Found exact match: ${analysis.resumeId} === ${resumeId}`)
                return true
              }
              // For tailored resumes, also check if it matches the job application ID
              if (resumeId.startsWith('tailored-')) {
                const jobAppIdFromResumeId = resumeId.replace('tailored-', '')
                const matches = analysis.resumeId === jobAppIdFromResumeId || analysis.resumeId === actualResumeId
                if (matches) {
                  console.log(`[Fit Score] Found tailored match: ${analysis.resumeId} matches ${resumeId} (jobAppId: ${jobAppIdFromResumeId}, actual: ${actualResumeId})`)
                }
                return matches
              }
              // For regular resumes, check actualResumeId
              const matches = analysis.resumeId === actualResumeId
              if (matches) {
                console.log(`[Fit Score] Found regular match: ${analysis.resumeId} === ${actualResumeId}`)
              }
              return matches
            }
          )
          
          // Debug logging
          if (pollCount % 10 === 0) { // Log every 20 seconds
            console.log(`[Fit Score] Polling... (${pollCount}/${maxPolls}, ${Math.round(elapsed/1000)}s elapsed)`, {
              resumeId,
              actualResumeId,
              availableResumeIds: updatedFitAnalysis?.resumeFitAnalyses?.map(a => a.resumeId) || [],
              found: !!resumeFitData
            })
          }
          
          if (resumeFitData) {
            // Found the fit data for this resume
            clearPolling()
            setFitAnalysis(updatedFitAnalysis)
            setGeneratingFitScores(prev => {
              const next = new Set(prev)
              next.delete(resumeId)
              return next
            })
            
            showMessageBox({
              type: 'success',
              title: 'Fit Score Generated',
              message: `Fit analysis completed successfully! Score: ${resumeFitData.fitScore}%`,
              buttons: ['ok'],
              onButtonClick: () => {}
            })
          } else if (pollCount >= maxPolls || elapsed >= maxWaitTime) {
            // Timeout reached
            clearPolling()
            setGeneratingFitScores(prev => {
              const next = new Set(prev)
              next.delete(resumeId)
              return next
            })
            
            // Check if we got any fit analysis results (maybe for other resumes)
            const hasAnyResults = updatedFitAnalysis?.resumeFitAnalyses && updatedFitAnalysis.resumeFitAnalyses.length > 0
            const message = hasAnyResults
              ? 'Fit analysis is still processing. Some results are available, but this resume may still be analyzing. Please refresh to check for updates.'
              : 'Fit analysis is taking longer than expected. Please refresh to check for updates.'
            
            showMessageBox({
              type: 'warning',
              title: 'Timeout',
              message,
              buttons: ['ok'],
              onButtonClick: () => {}
            })
          } else {
            // Still polling - update fit analysis if we have any new results
            if (updatedFitAnalysis?.resumeFitAnalyses && updatedFitAnalysis.resumeFitAnalyses.length > 0) {
              setFitAnalysis(updatedFitAnalysis)
            }
          }
        } catch (error) {
          console.error('Error polling for fit analysis:', error)
          // Don't stop polling on individual errors, just log them
          // But if we've been polling for a while and keep getting errors, maybe stop
          if (pollCount > 10 && elapsed > 30000) {
            console.error('Multiple polling errors, stopping poll after 30 seconds of errors')
            clearPolling()
            setGeneratingFitScores(prev => {
              const next = new Set(prev)
              next.delete(resumeId)
              return next
            })
          }
        }
      }, 2000) // Poll every 2 seconds

      // Store interval for cleanup
      fitScorePollIntervalsRef.current.set(resumeId, pollInterval)
    } catch (error) {
      setGeneratingFitScores(prev => {
        const next = new Set(prev)
        next.delete(resumeId)
        return next
      })
      setFitAnalysisError(error instanceof Error ? error.message : 'Failed to generate fit score')
      showMessageBox({
        type: 'error',
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to generate fit score. Please try again.',
        buttons: ['ok'],
        onButtonClick: () => {}
      })
    }
  }, [application.id, generatingFitScores, showMessageBox])

  // Only render portal after component is mounted (client-side only)
  // This prevents React error #310 (hydration mismatch)
  if (!isMounted || typeof document === 'undefined' || !document.body) {
    return null
  }

  const showDetailsPanel = isOpen && !!resumeDetailsResumeId && canOpenResumeDetails
  const showAssignmentPanel = isOpen && !showDetailsPanel

  if (!showAssignmentPanel && !showDetailsPanel) {
    return null
  }

  return createPortal(
    <>
      {showAssignmentPanel && (
        <div
          className="resume-assignment-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              onClose()
            }
          }}
        >
          <div
            className="resume-assignment-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="resume-assignment-modal-header">
              <div className="resume-assignment-modal-title">
                <h2>📄 Resume Assignment</h2>
                <p className="resume-assignment-modal-subtitle">
                  {application.position} at {application.company}
                </p>
              </div>
              <button
                className="resume-assignment-modal-close"
                onClick={onClose}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="resume-assignment-modal-content">
              {/* Non-blocking fit-analysis status */}
              {isLoadingFitAnalysis && (
                <div className="resume-assignment-loading">
                  <LoadingSpinner size="medium" />
                  <p>Refreshing resume fit analysis in the background. You can still assign a resume now.</p>
                </div>
              )}

              {fitAnalysisError && (
                <div className="resume-assignment-error">
                  <p>⚠️ {fitAnalysisError}</p>
                </div>
              )}

              {/* Currently Assigned Resume - Shows at the top */}
              {applicationData.id && (
                <div className="resume-assignment-assigned">
                  <h3>Currently Assigned Resume</h3>
                  <AssignedResumeViewer
                    jobApplicationId={applicationData.id}
                    selectedResumeId={applicationData.selectedResumeId || null}
                    resumeSelectionType={applicationData.resumeSelectionType || null}
                    availableResumes={availableResumes}
                    tailoredResume={tailoredResume}
                    fitAnalysis={fitAnalysis}
                    onEdit={() => {
                      // Scroll to resume selection list
                      const selectionElement = document.querySelector('.resume-assignment-selection')
                      if (selectionElement) {
                        selectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
                      }
                    }}
                    onViewDetails={
                      currentAssignedResumeDetailsId ? () => setResumeDetailsResumeId(currentAssignedResumeDetailsId) : undefined
                    }
                  />
                </div>
              )}

              {/* Available Resumes for Selection - Shows below assigned resume */}
              {applicationData.id && (
                <div className="resume-assignment-selection">
                  <div className="resume-assignment-selection-header">
                    <h3>Available Resumes</h3>
                  </div>
                  
                  <div className="resume-list">
                    {availableResumesForSelection.length === 0 ? (
                      <div className="resume-assignment-empty">
                        <p>No other resumes available. All resumes are assigned.</p>
                      </div>
                    ) : null}
                    {availableResumesForSelection.map((resume) => {
                        const fitData = getResumeFitScore(resume.id)
                        const isTailored = resume.id.startsWith('tailored-')
                        const isGenerating = generatingFitScores.has(resume.id)
                        const isConfirmingDelete = resumeDeleteConfirmId === resume.id
                        const isDeletingThis = isDeletingTailored && deletingTailoredId === resume.id
                        
                        return (
                          <div
                            key={resume.id}
                            className={`resume-list-item ${isSelectingResume ? 'disabled' : ''}`}
                            onClick={() => {
                              if (!isSelectingResume) {
                                handleResumeSelect(resume.id)
                              }
                            }}
                          >
                            <div className="resume-list-item-content">
                              <div className="resume-list-item-name">
                                {resume.originalFilename || resume.filename || 'Resume'}
                                {isTailored && <span className="resume-list-item-badge tailored">✨ Tailored</span>}
                              </div>
                              {fitData ? (
                                <div className={`resume-list-item-fit ${getFitScoreColor(fitData.fitScore)}`}>
                                  <span className="resume-list-item-fit-score">{fitData.fitScore}%</span>
                                </div>
                              ) : (
                                <div className="resume-list-item-fit no-data">
                                  {isGenerating ? (
                                    <span className="resume-generate-fit-loading">
                                      <LoadingSpinner size="small" />
                                      <span>Generating... (30-60s)</span>
                                    </span>
                                  ) : (
                                    <span>No fit data</span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="resume-list-item-actions">
                              {!fitData && !isGenerating && (
                                <button
                                  className="btn btn-secondary btn-sm resume-generate-fit-btn"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleGenerateFitScore(resume.id)
                                  }}
                                  disabled={isSelectingResume}
                                >
                                  Generate Fit Score
                                </button>
                              )}
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setResumeDetailsResumeId(resume.id)
                                }}
                                disabled={isSelectingResume}
                              >
                                View Details
                              </button>
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleResumeSelect(resume.id)
                                }}
                                disabled={isSelectingResume || isGenerating}
                              >
                                Select
                              </button>
                              {isTailored && (
                                isConfirmingDelete ? (
                                  <div className="resume-list-item-delete-confirm">
                                    <button
                                      className="btn btn-secondary btn-sm"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        setResumeDeleteConfirmId(null)
                                      }}
                                      disabled={isDeletingThis}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      className="btn btn-sm resume-delete-btn"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleDeleteResume(resume)
                                      }}
                                      disabled={isDeletingThis}
                                    >
                                      {isDeletingThis ? 'Deleting...' : 'Delete'}
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    className="btn btn-sm resume-delete-btn"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setResumeDeleteConfirmId(resume.id)
                                    }}
                                    disabled={isSelectingResume || isGenerating || isDeletingTailored}
                                  >
                                    Delete
                                  </button>
                                )
                              )}
                            </div>
                          </div>
                        )
                      })}
                      
                    {/* Always show "Generate Tailored Resume" so user can create one even when no other resumes or no fit data */}
                    <div
                      className="resume-list-item resume-list-item-generate-tailored"
                      onClick={() => setIsTailoredResumeWizardOpen(true)}
                    >
                      <div className="resume-list-item-content">
                        <div className="resume-list-item-name">
                          <span className="resume-generate-icon">✨</span>
                          Generate Tailored Resume
                        </div>
                        <div className="resume-list-item-fit no-data">
                          <span>Custom</span>
                        </div>
                      </div>
                      <div className="resume-list-item-actions">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            setIsTailoredResumeWizardOpen(true)
                          }}
                        >
                          Generate
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {!applicationData.id && (
                <div className="resume-assignment-no-id">
                  <p>Application must be saved before assigning a resume.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showDetailsPanel && (
        <div
          className="resume-assignment-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              onClose()
            }
          }}
        >
          <div
            className="resume-assignment-modal"
            onClick={(e) => e.stopPropagation()}
          >
              <div className="resume-assignment-modal-header">
                <div className="resume-assignment-modal-title">
                  <h2>📄 Resume Details</h2>
                  <p className="resume-assignment-modal-subtitle">
                    Review {previewedResume?.originalFilename || previewedResume?.filename || 'resume'} for {application.position} at {application.company}
                  </p>
                </div>
              <button
                className="resume-assignment-modal-close"
                onClick={onClose}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="resume-assignment-modal-content">
              <div className="resume-assignment-tailored-details">
                <div className="resume-assignment-tailored-header">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setResumeDetailsResumeId(null)}
                  >
                    ← Back to Resume Assignment
                  </button>
                </div>
                {applicationData.id && previewedResumeSelectionType && resumeDetailsResumeId && (
                  <AssignedResumeViewer
                    jobApplicationId={applicationData.id}
                    selectedResumeId={resumeDetailsResumeId}
                    resumeSelectionType={previewedResumeSelectionType}
                    availableResumes={availableResumes}
                    tailoredResume={tailoredResume}
                    fitAnalysis={fitAnalysis}
                    onEdit={() => setResumeDetailsResumeId(null)}
                    forceExpanded={true}
                    showChangeButton={false}
                    showViewDetailsButton={false}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tailored Resume Wizard - Render in separate portal with higher z-index */}
      {applicationData.id && isTailoredResumeWizardOpen && createPortal(
        <div className="tailored-resume-wizard-wrapper">
          <TailoredResumeWizard
            isOpen={isTailoredResumeWizardOpen}
            onClose={() => setIsTailoredResumeWizardOpen(false)}
            jobApplicationId={applicationData.id}
            fitAnalysis={fitAnalysis}
            availableResumes={availableResumes}
            onResumeGenerated={handleTailoredResumeGenerated}
          />
        </div>,
        document.body
      )}
    </>,
    document.body
  )
}
